// load devtool settings
import './devtools';

// definitions
import { routes } from 'defs';
import { RouteType } from 'defs/routes';
import appConfig from 'helper/mapBackendConfig';

// import asyncComponent from 'components/helper/AsyncComponent';
import { ProgramOverviewFilterValues } from 'components/views/ProgramOverview';
import App from 'containers/App';
import ConnectionDetector from 'containers/helper/Connection/Detector';
import Heartbeat from 'containers/helper/Heartbeat';
import Tutorial from 'containers/molecules/Tutorial';
import rootStoreInstance from './storeInstance';
import Terrific from './terrific';

// localization stuff
import moment from 'moment';
import 'moment/locale/de';
import 'moment/locale/fr';
import 'moment/locale/it';

// react & related frameworks
import * as React from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { presets, RouteTransition } from 'react-router-transition';
import asyncComponent from './components/helper/AsyncComponent/AsyncComponent';
import { initMigrosTokenProvider } from './helper/migros-token-provider';

(async () => {
	await initMigrosTokenProvider();
})();

const CardLayerTracking = asyncComponent(
	'CardLayerTracking',
	rootStoreInstance,
	() =>
		import(
			/* webpackChunkName: "card-layer-tracking-container" */ './containers/molecules/CardLayerTracking'
		) as any
);

const AnamnesisView = {
	AcceptHealthTarget: asyncComponent(
		'AcceptHealthTarget',
		rootStoreInstance,
		() =>
			import(
				/* webpackChunkName: "anamnesis-accept-health-target-container" */ './containers/organisms/Anamnesis/Steps/AcceptHealthTarget'
			) as any
	),
	DefineHealthTarget: asyncComponent(
		'DefineHealthTarget',
		rootStoreInstance,
		() =>
			import(
				/* webpackChunkName: "anamnesis-define-health-target-container" */ './containers/organisms/Anamnesis/Steps/DefineHealthTarget'
			) as any
	),
	SelectDietaryHabits: asyncComponent(
		'SelectDietaryHabits',
		rootStoreInstance,
		() =>
			import(
				/* webpackChunkName: "anamnesis-select-dietary-habits-container" */ './containers/organisms/Anamnesis/Steps/SelectDietaryHabits'
			) as any
	),
	DetermineAge: asyncComponent(
		'DetermineAge',
		rootStoreInstance,
		() =>
			import(
				/* webpackChunkName: "anamnesis-determine-age-container" */ './containers/organisms/Anamnesis/Steps/DetermineAge'
			) as any
	),
	DetermineHeight: asyncComponent(
		'DetermineHeight',
		rootStoreInstance,
		() =>
			import(
				/* webpackChunkName: "anamnesis-determine-height-container" */ './containers/organisms/Anamnesis/Steps/DetermineHeight'
			) as any
	),
	DetermineWeight: asyncComponent(
		'DetermineWeight',
		rootStoreInstance,
		() =>
			import(
				/* webpackChunkName: "anamnesis-determine-weight-container" */ './containers/organisms/Anamnesis/Steps/DetermineWeight'
			) as any
	),
	SelectActivityLevel: asyncComponent(
		'SelectActivityLevel',
		rootStoreInstance,
		() =>
			import(
				/* webpackChunkName: "anamnesis-select-activity-level-container" */ './containers/organisms/Anamnesis/Steps/SelectActivityLevel'
			) as any
	),
	SelectCoach: asyncComponent(
		'SelectCoach',
		rootStoreInstance,
		() =>
			import(
				/* webpackChunkName: "anamnesis-select-coach-container" */ './containers/organisms/Anamnesis/Steps/SelectCoach'
			) as any
	),
	SelectGender: asyncComponent(
		'SelectGender',
		rootStoreInstance,
		() =>
			import(
				/* webpackChunkName: "anamnesis-select-gender-container" */ './containers/organisms/Anamnesis/Steps/SelectGender'
			) as any
	),
	Start: asyncComponent(
		'Start',
		rootStoreInstance,
		() =>
			import(
				/* webpackChunkName: "anamnesis-start-container" */ './containers/organisms/Anamnesis/Steps/Start'
			) as any
	),
	Verify: asyncComponent(
		'Verify',
		rootStoreInstance,
		() =>
			import(
				/* webpackChunkName: "anamnesis-verify-container" */ './containers/organisms/Anamnesis/Steps/Verify'
			) as any
	),
	Dsgvo: asyncComponent(
		'Dsgvo',
		rootStoreInstance,
		() =>
			import(
				/* webpackChunkName: "anamnesis-dsgvo-container" */ './containers/organisms/Anamnesis/Steps/Dsgvo'
			) as any
	),
	Disclaimer: asyncComponent(
		'Disclaimer',
		rootStoreInstance,
		() =>
			import(
				/* webpackChunkName: "anamnesis-disclaimer-container" */ './containers/organisms/Anamnesis/Steps/Disclaimer'
			) as any
	),
};

const LoginView = asyncComponent(
	'LoginView',
	rootStoreInstance,
	() => import(/* webpackChunkName: "login-view-container" */ './containers/views/LoginView') as any
);
const ProfileView = asyncComponent(
	'ProfileView',
	rootStoreInstance,
	() => import(/* webpackChunkName: "profile-view-container" */ './containers/views/ProfileView') as any
);
const Error40x = asyncComponent(
	'Error40x',
	rootStoreInstance,
	() => import(/* webpackChunkName: "error-view-component" */ './components/views/Error40x') as any
);

const ProgramFeedView = asyncComponent(
	'ProgramFeedView',
	rootStoreInstance,
	() => import(/* webpackChunkName: "program-feed-view-container" */ './containers/views/ProgramFeedView') as any
);

const ProgramFeedRedirectView = asyncComponent(
	'ProgramFeedRedirectView',
	rootStoreInstance,
	() =>
		import(
			/* webpackChunkName: "program-feed-redirect-view-container" */ './containers/views/ProgramFeedRedirectView'
		) as any
);

const ProgramPreview = asyncComponent(
	'ProgramPreview',
	rootStoreInstance,
	() => import(/* webpackChunkName: "program-preview-view-container" */ './containers/views/ProgramPreview') as any
);
const ProgramOverview = asyncComponent(
	'ProgramOverview',
	rootStoreInstance,
	() => import(/* webpackChunkName: "program-overview-view-container" */ './containers/views/ProgramOverview') as any
);
const MySuccess = asyncComponent(
	'MySuccess',
	rootStoreInstance,
	() => import(/* webpackChunkName: "my-success-view-container" */ './containers/views/MySuccessView') as any
);
const FavoritesView = asyncComponent(
	'FavoritesView',
	rootStoreInstance,
	() => import(/* webpackChunkName: "favorites-view-container" */ './containers/views/FavoritesView') as any
);
const FavoritesDetailView = asyncComponent(
	'FavoritesDetailView',
	rootStoreInstance,
	() =>
		import(
			/* webpackChunkName: "favorites-detail-view-container" */ './containers/views/FavoritesDetailView'
		) as any
);
const DashboardView = asyncComponent(
	'DashboardView',
	rootStoreInstance,
	() => import(/* webpackChunkName: "dashboard-view-container" */ './containers/views/DashboardView') as any
);
const DashboardNewMeasurementView = asyncComponent(
	'DashboardNewMeasurementView',
	rootStoreInstance,
	() =>
		import(
			/* webpackChunkName: "dashboard-new-measurement-view-container" */ './containers/views/DashboardNewMeasurementView'
		) as any
);
const DeepLinkStartProgram = asyncComponent(
	'DeepLinkStartProgram',
	rootStoreInstance,
	() =>
		import(
			/* webpackChunkName: "deep-link-start-program-view-container" */ './containers/views/DeepLinkStartProgramView'
		) as any
);
const PatchView = asyncComponent(
	'PatchView',
	rootStoreInstance,
	() => import(/* webpackChunkName: "patch-view-component" */ './components/views/PatchView') as any
);

const PoolRootRenderer = asyncComponent(
	'PoolRootRenderer',
	rootStoreInstance,
	() =>
		import(
			/* webpackChunkName: "pool-root-renderer-view-component" */ './components/views/PoolView/PoolRootRenderer'
		) as any
);
const PoolDetailView = asyncComponent(
	'PoolDetailView',
	rootStoreInstance,
	() => import(/* webpackChunkName: "pool-detail-view-container" */ './containers/views/PoolDetailView') as any
);
const PoolCustomWorkoutView = asyncComponent(
	'PoolCustomWorkoutView',
	rootStoreInstance,
	() =>
		import(
			/* webpackChunkName: "pool-custo-workout-view-container" */ './containers/views/PoolCustomWorkoutView'
		) as any
);

moment.locale(appConfig.env.language);

function withApp<P extends object = Object>(View: any, matchingRouteConfig: RouteType, viewProps?: P) {
	return (props) => {
		return (
			<App store={rootStoreInstance} matchingRouteConfig={matchingRouteConfig}>
				<CardLayerTracking />
				<View locationKey={props.location.pathname} {...(viewProps || {})} />
			</App>
		);
	};
}

function withAppAndTransition<P extends object = Object>(
	View: any,
	matchingRouteConfig: RouteType,
	preset: any,
	viewProps?: P
) {
	return (props) => (
		<App store={rootStoreInstance} matchingRouteConfig={matchingRouteConfig}>
			<RouteTransition
				locationKey={props.location.pathname}
				runOnMount
				pathname={matchingRouteConfig.href}
				{...preset}
			>
				<CardLayerTracking />
				<View {...(viewProps || {})} />
			</RouteTransition>
		</App>
	);
}

const domNode = document.getElementById('root');

if (domNode !== null) {
	render(
		<React.StrictMode>
			<Provider store={rootStoreInstance}>
				<div>
					<ConnectionDetector />
					<Heartbeat />
					<Terrific>
						<BrowserRouter basename={appConfig.env.baseName}>
							<Switch>
								<Route path={'/patch-url-to/:id'} component={PatchView} />
								<Route
									exact
									path={routes.dashboard.indexRoute.href}
									render={withApp(DashboardView, routes.dashboard.indexRoute)}
								/>
								<Route
									exact
									path={routes.dashboard.newMeasurement.href}
									render={withAppAndTransition(
										DashboardNewMeasurementView,
										routes.dashboard.newMeasurement,
										presets.fade
									)}
								/>
								<Route
									exact
									path={routes.deepLink.startProgram.href}
									render={withApp(DeepLinkStartProgram, routes.deepLink.startProgram)}
								/>
								<Route
									exact
									path={routes.dashboard.indexRoute.href}
									render={withApp(DashboardView, routes.dashboard.indexRoute)}
								/>
								<Route
									exact
									path={routes.dashboard.newMeasurement.href}
									render={withAppAndTransition(
										DashboardNewMeasurementView,
										routes.dashboard.newMeasurement,
										presets.fade
									)}
								/>
								<Route
									exact
									path={routes.pool.indexRoute.href}
									render={withAppAndTransition(
										PoolRootRenderer,
										routes.pool.indexRoute,
										presets.fade
									)}
								/>
								<Route
									exact
									path={routes.pool.detailRoute.href}
									render={withAppAndTransition(PoolDetailView, routes.pool.detailRoute, presets.fade)}
								/>
								<Route
									exact
									path={routes.pool.customWorkoutRoute.href}
									render={withAppAndTransition(
										PoolCustomWorkoutView,
										routes.pool.customWorkoutRoute,
										presets.fade
									)}
								/>
								<Route
									exact
									path={routes.mySuccess.indexRoute.href}
									render={withApp(MySuccess, routes.mySuccess.indexRoute)}
								/>
								<Route
									exact
									path={routes.today.indexRoute.href}
									render={withApp(ProgramFeedRedirectView, routes.today.indexRoute)}
								/>
								<Route
									exact
									path={routes.today.day.href}
									render={withApp(ProgramFeedView, routes.today.day)}
								/>
								<Redirect
									exact
									from={routes.program.indexRoute.href}
									to={routes.program.recommended.href}
								/>
								<Redirect
									exact
									from={`${routes.program.indexRoute.href}/`}
									to={routes.program.recommended.href}
								/>
								<Route
									exact
									path={routes.program.recommended.href}
									render={withApp(ProgramOverview, routes.program.recommended, {
										id: ProgramOverviewFilterValues.RECOMMENDED,
									})}
								/>
								<Route
									exact
									path={routes.program.further.href}
									render={withApp(ProgramOverview, routes.program.further, {
										id: ProgramOverviewFilterValues.FURTHER,
									})}
								/>
								<Route
									exact
									path={routes.program.preview.href}
									render={withApp(ProgramPreview, routes.program.preview)}
								/>
								<Route
									exact
									path={routes.login.indexRoute.href}
									render={withApp(LoginView, routes.login.indexRoute)}
								/>
								<Route
									exact
									path={routes.profile.indexRoute.href}
									render={withApp(ProfileView, routes.profile.indexRoute)}
								/>
								<Redirect
									exact
									from={routes.anamnesis.indexRoute.href}
									to={routes.anamnesis.selectCoach.href}
								/>
								<Redirect
									exact
									from={`${routes.anamnesis.indexRoute.href}/`}
									to={routes.anamnesis.selectCoach.href}
								/>
								<Route
									exact
									path={routes.anamnesis.start.href}
									render={withApp(AnamnesisView.Start, routes.anamnesis.start)}
								/>
								<Route
									exact
									path={routes.anamnesis.selectCoach.href}
									render={withApp(AnamnesisView.SelectCoach, routes.anamnesis.selectCoach)}
								/>
								<Route
									exact
									path={routes.anamnesis.selectGender.href}
									render={withApp(AnamnesisView.SelectGender, routes.anamnesis.selectGender)}
								/>
								<Route
									exact
									path={routes.anamnesis.determineAge.href}
									render={withApp(AnamnesisView.DetermineAge, routes.anamnesis.determineAge)}
								/>
								<Route
									exact
									path={routes.anamnesis.determineHeight.href}
									render={withApp(AnamnesisView.DetermineHeight, routes.anamnesis.determineHeight)}
								/>
								<Route
									exact
									path={routes.anamnesis.determineWeight.href}
									render={withApp(AnamnesisView.DetermineWeight, routes.anamnesis.determineWeight)}
								/>
								<Route
									exact
									path={routes.anamnesis.defineHealthTarget.href}
									render={withApp(
										AnamnesisView.DefineHealthTarget,
										routes.anamnesis.defineHealthTarget
									)}
								/>
								<Route
									exact
									path={routes.anamnesis.acceptHealthTarget.href}
									render={withApp(
										AnamnesisView.AcceptHealthTarget,
										routes.anamnesis.acceptHealthTarget
									)}
								/>
								<Route
									exact
									path={routes.anamnesis.selectDietaryHabits.href}
									render={withApp(
										AnamnesisView.SelectDietaryHabits,
										routes.anamnesis.selectDietaryHabits
									)}
								/>
								<Route
									exact
									path={routes.anamnesis.selectActivityLevel.href}
									render={withApp(
										AnamnesisView.SelectActivityLevel,
										routes.anamnesis.selectActivityLevel
									)}
								/>
								<Route
									exact
									path={routes.anamnesis.verify.href}
									render={withApp(AnamnesisView.Verify, routes.anamnesis.verify)}
								/>
								<Route
									exact
									path={routes.anamnesis.dsgvo.href}
									render={withApp(AnamnesisView.Dsgvo, routes.anamnesis.dsgvo)}
								/>
								<Route
									exact
									path={routes.anamnesis.disclaimer.href}
									render={withApp(AnamnesisView.Disclaimer, routes.anamnesis.disclaimer)}
								/>
								<Route
									exact
									path={routes.favorites.indexRoute.href}
									render={withApp(FavoritesView, routes.favorites.indexRoute)}
								/>
								<Route
									exact
									path={routes.favorites.cluster.href}
									render={withApp(FavoritesView, routes.favorites.cluster)}
								/>
								<Route
									exact
									path={routes.favorites.detail.href}
									render={withAppAndTransition(
										FavoritesDetailView,
										routes.favorites.detail,
										presets.fade
									)}
								/>
								<Route render={withApp(Error40x, routes.error40x.indexRoute)} />
							</Switch>
						</BrowserRouter>
					</Terrific>
					<Tutorial />
				</div>
			</Provider>
		</React.StrictMode>,
		domNode
	);
}
